import { useEffect, useState } from 'react';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography } from '@mui/material';
import { fmtPrice } from './seasonalMenuShared';

// ─── OrderEditDialog ──────────────────────────────────────────────────────────

function OrderEditDialog({ open, onClose, store, selectedMenu, existingOrder, onSave }) {
  const [participating, setParticipating] = useState(true);
  const [itemPrices, setItemPrices] = useState([]);
  const [memo, setMemo] = useState('');

  useEffect(() => {
    if (open && selectedMenu) {
      const items = (selectedMenu.items || []);
      if (existingOrder) {
        setParticipating(existingOrder.participating !== false);
        setMemo(existingOrder.memo || '');
        // build itemPrices from existing order, falling back to basePrice
        const merged = items.map(it => {
          const found = (existingOrder.itemPrices || []).find(ip => ip.itemName === it.name);
          return {
            itemName: it.name,
            basePrice: it.basePrice || 0,
            finalPrice: found ? String(found.finalPrice) : String(it.basePrice || 0),
          };
        });
        setItemPrices(merged);
      } else {
        setParticipating(true);
        setMemo('');
        setItemPrices(items.map(it => ({
          itemName: it.name,
          basePrice: it.basePrice || 0,
          finalPrice: String(it.basePrice || 0),
        })));
      }
    }
  }, [open, selectedMenu, existingOrder]);

  const handlePriceChange = (idx, val) => {
    setItemPrices(prev => prev.map((ip, i) => i === idx ? { ...ip, finalPrice: val } : ip));
  };

  const handleSave = () => {
    const prices = itemPrices.map(ip => ({
      itemName: ip.itemName,
      basePrice: ip.basePrice,
      finalPrice: Number(ip.finalPrice) || ip.basePrice,
    }));
    onSave({ participating, itemPrices: prices, memo });
  };

  const storeName = store?.tljNo || store?.ownerName || store?.name || '점포';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: '#551FFF' }}>
        점포 신청 {existingOrder ? '수정' : '등록'} — {storeName}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {/* 판매 여부 */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>판매 여부</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <button
              type="button"
              onClick={() => setParticipating(true)}
              className={`filter-btn${participating ? ' filter-btn--active' : ''}`}
              style={{ padding: '8px 20px', fontSize: 14, fontWeight: 700 }}
            >
              판매
            </button>
            <button
              type="button"
              onClick={() => setParticipating(false)}
              className={`filter-btn${!participating ? ' filter-btn--active filter-btn--no-sale' : ''}`}
              style={{ padding: '8px 20px', fontSize: 14, fontWeight: 700 }}
            >
              판매 안함
            </button>
          </Box>

          {/* 아이템별 가격 */}
          {participating && itemPrices.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>항목별 판매가</Typography>
              {itemPrices.map((ip, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 2, mb: 1.5,
                    p: 1.5, background: '#fafafa', borderRadius: 2, border: '1px solid #eee',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{ip.itemName}</Typography>
                    <Typography variant="caption" sx={{ color: '#aaa' }}>
                      기준가: {fmtPrice(ip.basePrice)}
                    </Typography>
                  </Box>
                  <TextField
                    label="판매가 (₩)" type="number"
                    value={ip.finalPrice}
                    onChange={e => handlePriceChange(idx, e.target.value)}
                    size="small"
                    sx={{ width: 140 }}
                  />
                </Box>
              ))}
            </>
          )}

          {/* 메모 */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, mt: 1 }}>메모</Typography>
          <TextField
            fullWidth multiline rows={2} size="small"
            placeholder="전달사항 (선택)"
            value={memo} onChange={e => setMemo(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: '#888' }}>취소</Button>
        <Button
          variant="contained" onClick={handleSave}
          sx={{ background: '#551FFF', borderRadius: 2 }}
        >
          저장
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default OrderEditDialog;
